import React from 'react';
import OrderBillSender from './OrderBillSender';

const DetailedOrdersModal = ({ orders, onClose }) => {
  if (!orders || orders.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-md p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-stone-200 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden ring-1 ring-stone-900/5">
        {/* Sticky modal header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3.5 sm:px-6 sm:py-4 border-b border-stone-200 bg-gradient-to-r from-stone-50 via-orange-50/40 to-stone-50 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white shadow-sm">
              <span className="text-base" aria-hidden>📋</span>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                Complete order details
              </p>
              <p className="mt-0.5 text-sm font-medium text-stone-700">
                {orders.length === 1
                  ? 'Review the full bill for this order'
                  : `${orders.length} orders · review bills`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 shadow-sm hover:bg-stone-100 hover:text-stone-800 hover:border-stone-300 transition-all"
            aria-label="Close details"
          >
            ×
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-4 sm:px-6 sm:pt-5 sm:pb-6 space-y-5">
          {orders.map((order, index) => {
            const baseTotal =
              order.totalAmount ??
              (order.items?.reduce(
                (sum, item) => sum + item.price * item.quantity,
                0
              ) || 0);

            return (
              <div
                key={order._id || index}
                className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-stone-50/80 p-4 sm:p-5 space-y-4 shadow-sm"
              >
                {/* Orange accent stripe */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-500 rounded-l-xl" />
                {/* Order header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pl-1">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-sm font-bold text-white shadow-sm">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                        Order summary
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-stone-800">
                        {order.customerName || 'Walk‑in customer'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 pl-1">
                    <span className="inline-flex items-center rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 border border-sky-100/80">
                      📅 {order.orderDate
                        ? new Date(order.orderDate).toLocaleDateString()
                        : 'Date not available'}
                    </span>
                    <span className="inline-flex items-center rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 border border-emerald-200 shadow-sm">
                      ₹{order.finalAmount?.toFixed(2) ?? '0.00'}
                    </span>
                  </div>
                </div>

                {/* Customer & bill details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 pl-1">
                  <div className="rounded-xl bg-white/80 border border-stone-100 p-3.5 sm:p-4 space-y-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500 mb-1">
                      Customer & order
                    </p>
                    <div className="flex items-center gap-2.5 py-1">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-stone-100 text-xs">👤</span>
                      <span className="text-xs font-medium text-stone-800">
                        {order.customerName || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5 py-1">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-stone-100 text-xs">📱</span>
                      <span className="text-xs text-stone-700">
                        {order.mobileNumber || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5 py-1">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-stone-100 text-xs">🪑</span>
                      <span className="text-xs text-stone-700">
                        {order.tableId?.displayName ||
                          order.tableId?.tableName ||
                          order.tableId?.tableNumber ||
                          'Order from counter'}
                      </span>
                    </div>
                    <div className="flex items-start gap-2.5 py-1">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-xs mt-0.5">💬</span>
                      <span className="text-xs text-stone-600">
                        {order.message || 'No special message'}
                      </span>
                    </div>
                    {order.appliedOffer && (
                      <div className="flex items-center gap-2.5 rounded-lg bg-orange-50/80 px-3 py-2 border border-orange-100">
                        <span className="text-sm">🎁</span>
                        <span className="text-xs font-medium text-orange-700">
                          {order.appliedOffer.title}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl bg-emerald-50/50 border border-emerald-100 p-3.5 sm:p-4 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-800/80 mb-1">
                      Bill breakdown
                    </p>
                    <div className="flex justify-between text-xs py-1">
                      <span className="text-stone-600">Base total</span>
                      <span className="font-semibold text-stone-800">
                        ₹{baseTotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs py-1">
                      <span className="text-stone-600">
                        Tax{order.taxPercentage != null ? ` (${order.taxPercentage}%)` : ''}
                      </span>
                      <span className="font-semibold text-stone-800">
                        ₹{order.taxAmount?.toFixed(2) ?? '0.00'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs py-1">
                      <span className="text-stone-600">Platform fees</span>
                      <span className="font-semibold text-stone-800">
                        ₹{order.serviceAmount && order.serviceAmount > 0 ? order.serviceAmount.toFixed(2) : '0.00'}
                      </span>
                    </div>
                    {order.offerDiscount > 0 && (
                      <div className="flex justify-between text-xs py-1 text-emerald-700">
                        <span>Offer discount</span>
                        <span className="font-semibold">−₹{order.offerDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="mt-2 pt-3 border-t-2 border-emerald-200 flex justify-between items-center rounded-lg bg-emerald-100/50 px-3 py-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                        Final total
                      </span>
                      <span className="text-base font-bold text-emerald-800">
                        ₹{order.finalAmount?.toFixed(2) ?? '0.00'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ordered items */}
                <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-4 space-y-3 pl-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500 flex items-center gap-2">
                      <span className="text-sm">🍽️</span> Ordered items
                    </p>
                    <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-[11px] font-medium text-stone-600">
                      {order.items?.length || 0} item{order.items && order.items.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {order.items?.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-lg bg-stone-50 hover:bg-stone-100/80 px-3 py-2.5 text-xs border border-stone-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-orange-100 text-orange-600 text-[10px] font-bold">
                            {item.quantity}
                          </span>
                          <div>
                            <span className="font-medium text-stone-800">{item.name}</span>
                            {item.note && (
                              <span className="block text-[10px] text-stone-500">{item.note}</span>
                            )}
                          </div>
                        </div>
                        <span className="font-semibold text-emerald-700">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Send bill options */}
                <div className="rounded-xl bg-gradient-to-br from-stone-50 to-orange-50/30 border border-stone-200 p-4 sm:p-4 pl-1">
                  <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500 flex items-center gap-1.5">
                    <span>📤</span> Share bill with customer
                  </p>
                  <OrderBillSender
                    orderId={order._id}
                    order={order}
                    showFeedback={false}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 z-10 border-t border-stone-200 bg-white/98 backdrop-blur-sm px-4 py-4 sm:px-6 sm:py-4 rounded-b-2xl shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-md hover:bg-orange-600 hover:shadow-lg active:scale-[0.99] transition-all"
          >
            Close details
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailedOrdersModal;
